import { useEffect, useMemo, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { PortfolioBookablePeriod, PortfolioProductCharging, PortfolioServiceType } from '@mews-commander/data-access';
import { Option } from '@mews-commander/types';
import { lt } from '@mews/framework';
import { getProductChargingTextKeyForBookableServices } from '@mews/business-components';
import { TextKey } from '@mews-commander/enums';
import { getCurrentServiceFieldName, ServicesFieldNames } from '../forms/schemas/configureProductFormSchema';
import { getPaymentChargingOptions, getConsumptionMomentOptions } from './steps/services/paymentsCharging';
import { portfolioProductChargingToProductChargingModeMap, portfolioBookablePeriodToTextKeyMap } from './utils';

export function useResolvedBookablePeriod(serviceKey?: string): PortfolioBookablePeriod | undefined {
    const { watch } = useFormContext<FieldValues>();

    const fieldName = serviceKey
        ? `${getCurrentServiceFieldName(serviceKey)}.${ServicesFieldNames.BookablePeriod}`
        : `${ServicesFieldNames.BookablePeriod}`;

    return watch(fieldName) ?? undefined;
}

export interface UsePaymentChargingOptionsProps {
    options: Option[];
    subOptions: Option[] | null;
    selectedOption: PortfolioProductCharging;
    chargingFieldName: string;
    consumptionFieldName: string;
}

export interface UsePaymentChargingOptionsParams {
    selectedService?: string;
    serviceType?: PortfolioServiceType;
    bookablePeriod?: PortfolioBookablePeriod;
}

export function usePaymentChargingOptions({
    selectedService,
    serviceType,
    bookablePeriod,
}: UsePaymentChargingOptionsParams): UsePaymentChargingOptionsProps {
    const { watch, setValue } = useFormContext<FieldValues>();
    const prefix = selectedService ? `${getCurrentServiceFieldName(selectedService)}.` : '';

    const currentServiceType: PortfolioServiceType = serviceType ?? watch(`${prefix}${ServicesFieldNames.ServiceType}`);
    const currentBookablePeriod: PortfolioBookablePeriod | undefined =
        bookablePeriod ?? (watch(`${prefix}${ServicesFieldNames.BookablePeriod}`) || undefined);

    const chargingFieldName = `${prefix}${ServicesFieldNames.Charging}`;
    const consumptionFieldName = `${prefix}${ServicesFieldNames.ConsumptionMoment}`;

    const selectedOption = watch(chargingFieldName) as PortfolioProductCharging;
    const [subOptions, setSubOptions] = useState<Option[] | null>(null);

    const options: Option[] = useMemo(
        () =>
            getPaymentChargingOptions(currentServiceType, currentBookablePeriod).map((option) => {
                const chargingMode = portfolioProductChargingToProductChargingModeMap[option];
                const periodTextKey = currentBookablePeriod
                    ? portfolioBookablePeriodToTextKeyMap[currentBookablePeriod]
                    : TextKey.Day;

                return {
                    label: lt(getProductChargingTextKeyForBookableServices(chargingMode, periodTextKey), {
                        timeUnit: periodTextKey,
                    }),
                    value: option,
                    disabled: false,
                    secondaryText: undefined,
                    indent: false,
                    inactive: false,
                };
            }),
        [currentBookablePeriod, currentServiceType]
    );

    useEffect(() => {
        if (!selectedOption && options.length === 1) {
            setValue(chargingFieldName, options[0].value);
        }
    }, [chargingFieldName, selectedOption, options, setValue]);

    useEffect(() => {
        if (!selectedOption) return;
        const selectedOptionData = getConsumptionMomentOptions(
            currentServiceType,
            currentBookablePeriod,
            selectedOption
        );
        setSubOptions(selectedOptionData as Option[]);
        if (selectedOptionData.length === 1) {
            setValue(consumptionFieldName, selectedOptionData[0].value);
        }
    }, [consumptionFieldName, currentBookablePeriod, currentServiceType, selectedOption, setValue]);

    return { options, subOptions, selectedOption, chargingFieldName, consumptionFieldName };
}
